import { Currency, DEFAULT_CURRENCY } from '../models/Organization';

/**
 * Per-currency formatting metadata. `locale` drives Intl grouping/decimal
 * conventions and symbol placement (e.g. Polish złoty is a suffix with a
 * comma decimal separator), `symbol` is a short glyph for compact labels
 * (form field labels, select options), and `label` names the currency for
 * the settings dropdown.
 */
export const CURRENCY_META: Record<Currency, { symbol: string; locale: string; label: string }> = {
  USD: { symbol: '$', locale: 'en-US', label: 'US Dollar (USD)' },
  EUR: { symbol: '€', locale: 'en-IE', label: 'Euro (EUR)' },
  GBP: { symbol: '£', locale: 'en-GB', label: 'British Pound (GBP)' },
  CAD: { symbol: 'CA$', locale: 'en-CA', label: 'Canadian Dollar (CAD)' },
  AUD: { symbol: 'A$', locale: 'en-AU', label: 'Australian Dollar (AUD)' },
  PLN: { symbol: 'zł', locale: 'pl-PL', label: 'Polish Złoty (PLN)' },
  CHF: { symbol: 'CHF', locale: 'de-CH', label: 'Swiss Franc (CHF)' },
  NZD: { symbol: 'NZ$', locale: 'en-NZ', label: 'New Zealand Dollar (NZD)' },
};

/** Ordered list of supported currencies for building selectors. */
export const SUPPORTED_CURRENCIES = Object.keys(CURRENCY_META) as Currency[];

/** Narrow an arbitrary value to a known Currency, falling back to the default. */
function resolveCurrency(currency: Currency | string | null | undefined): Currency {
  if (currency && Object.prototype.hasOwnProperty.call(CURRENCY_META, currency)) {
    return currency as Currency;
  }
  return DEFAULT_CURRENCY;
}

/** Short currency symbol/glyph (e.g. `$`, `€`, `zł`) — for compact labels, not full amounts. */
export function getCurrencySymbol(currency: Currency | string | null | undefined): string {
  return CURRENCY_META[resolveCurrency(currency)].symbol;
}

export interface FormatCurrencyOptions {
  /** Minimum fraction digits (default 2). */
  minimumFractionDigits?: number;
  /** Maximum fraction digits (default 2). Pass 0 for whole-number amounts. */
  maximumFractionDigits?: number;
}

/**
 * Format a monetary amount using the currency's native locale conventions.
 * Unknown/undefined currencies fall back to the org default so callers never
 * need to guard. Non-finite amounts render as an em dash.
 *
 * @example formatCurrency(1234.5, 'EUR') // "€1,234.50"
 * @example formatCurrency(120, 'PLN', { maximumFractionDigits: 0 }) // "120 zł"
 */
export function formatCurrency(
  amount: number | null | undefined,
  currency: Currency | string | null | undefined,
  options: FormatCurrencyOptions = {}
): string {
  const resolved = resolveCurrency(currency);
  if (amount == null || !Number.isFinite(amount)) {
    return '—';
  }
  const { locale } = CURRENCY_META[resolved];
  const minimumFractionDigits = options.minimumFractionDigits ?? 2;
  const maximumFractionDigits = options.maximumFractionDigits ?? 2;
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: resolved,
      minimumFractionDigits: Math.min(minimumFractionDigits, maximumFractionDigits),
      maximumFractionDigits,
    }).format(amount);
  } catch {
    // Defensive: an environment without full ICU data can throw on some
    // locale/currency pairs — fall back to a plain symbol-prefixed amount.
    return `${getCurrencySymbol(resolved)}${amount.toFixed(maximumFractionDigits)}`;
  }
}
